import { Box, Typography } from '@mui/material'
import React, { useState } from 'react'

import { MonitorSearchField } from 'components/status-checks/common/MonitorSearchField'
import { MonitorSearchList } from 'components/status-checks/common/MonitorSearchList'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { useTranslation } from 'hooks/use-translation'
import { DatadogMonitorsResponse } from 'types/api/datadog/monitors'
import { messageFromResponseError } from 'utils/strings'

export const DatadogMonitorSearch: React.FC<{
  domain: string
  integrationName: string
  onSelected(monitorId: string): void
  initialSearchValue?: string
  disabled?: boolean
}> = ({
  initialSearchValue = '',
  domain,
  integrationName,
  onSelected,
  disabled
}) => {
  const { trans } = useTranslation()
  const [searchValue, setSearchValue] = useState(initialSearchValue || '')
  const [queryValue, setQueryValue] = useState(initialSearchValue || '')

  const { currentTeamId } = useAuth()

  // for now we're limiting it to displaying only 50 monitors
  // this is somewhat arbitrary - the intent is to get the user to narrow down their search
  // we want to avoid having to add complex pagination-handling logic here at first
  const MAX_MONITORS_RETRIEVED = 50

  const {
    data: monitorResponse,
    isFetching: isFetchingMonitors,
    error: monitorsError
  } = useGremlinQuery<DatadogMonitorsResponse>(
    `/datadog/monitors?teamId=${currentTeamId}&domain=${domain}&searchString=${queryValue}&page=0&perPage=${MAX_MONITORS_RETRIEVED}&datadogIntegrationName=${integrationName}`,
    {},
    {
      enabled: Boolean(queryValue),
      placeholderData: {} as DatadogMonitorsResponse
    }
  )

  return (
    <Box>
      <MonitorSearchField
        disabled={disabled}
        isLoading={isFetchingMonitors}
        value={searchValue}
        onChange={(newValue) => setSearchValue(newValue)}
        onSubmit={() => setQueryValue(searchValue)}
      />
      {queryValue && !isFetchingMonitors && !disabled && (
        <>
          <MonitorSearchList
            errorMessage={
              monitorsError &&
              messageFromResponseError(monitorsError, 'Monitor search failed')
            }
            hasSearched={Boolean(queryValue)}
            isLoading={isFetchingMonitors}
            monitors={monitorResponse?.monitors ?? []}
            onClick={(monitorId) => {
              onSelected(monitorId)
            }}
          />

          {!isFetchingMonitors &&
            !monitorsError?.message &&
            monitorResponse?.metadata?.total_count > 0 &&
            monitorResponse.metadata.total_count > MAX_MONITORS_RETRIEVED && (
              <Typography variant="body2">
                {trans('status_checks.monitors.list.too_many_found', {
                  numFound: monitorResponse.metadata.total_count,
                  numShown: MAX_MONITORS_RETRIEVED
                })}
              </Typography>
            )}
        </>
      )}
    </Box>
  )
}
